import type { DispatchType } from 'frontend/store/utils/dispatchType';
import type { CustomWindow } from 'frontend/utils/types/customWindow';
import { datadogRum } from '@datadog/browser-rum';
import { DateTime } from '@ksyos/date-time';
import { Outlet, useLocation } from '@ksyos/router';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { IProcess } from '../../common/interfaces/IProcess';
import type { IRequest } from '../../common/interfaces/IRequest';
import type { IMaybeLoggedInReduxState, IReduxState } from '../configureStore';
import '../assets/styles/global.scss';
import { ServiceCode } from '../../common/enums/ServiceCode';
import {
    fetchAuthenticationAction,
    receiveUserActions,
    receiveUserInsurance,
    receiveUserOsasMraReferralLetter,
    receiveUserPermissions,
    receiveUserProcesses,
    receiveUserRequestProcesses,
} from '../store/authentication/actions';
import { fetch, fetchJson } from '../store/fetch';
import { Timeout } from '../store/Timeout';
import { isPendingRequestForService } from '../utils/isPendingRequestForService';
import { CatchingComponent } from './elements/CatchingComponent';
import { Logo } from './elements/molecules/logo/Logo';

const customWindow: CustomWindow = window;

export function App() {
    const dispatch: DispatchType = useDispatch();
    const { pathname, search } = useLocation();
    DateTime.setDefaultLocale('NL');

    const isChecking = useSelector((state: IMaybeLoggedInReduxState) => state.authentication.isChecking);
    const loggedIn = useSelector((state: IMaybeLoggedInReduxState) => state.authentication.loggedIn);
    const user = useSelector((state: IMaybeLoggedInReduxState) => state.authentication.user);
    const processes = useSelector((state: IReduxState) => state.authentication.processes);

    const isOsasFlowStarted = Boolean(
        processes?.find((process: IProcess) => process.service.code === ServiceCode.OSAS)
    );

    useEffect(() => {
        const _paq = (customWindow._paq = customWindow._paq ?? []);
        const analyticsSiteId = 3;
        const analyticsUrl = customWindow?.ksyosSettings?.analyticsUrl;

        _paq.push(['trackPageView']);
        _paq.push(['enableLinkTracking']);
        _paq.push(['setTrackerUrl', `${analyticsUrl}/matomo.php`]);
        _paq.push(['setSiteId', analyticsSiteId]);

        const scriptElement = document.createElement('script');
        scriptElement.type = 'text/javascript';
        scriptElement.async = true;
        scriptElement.defer = true;
        scriptElement.src = `${analyticsUrl}/matomo.js`;
        document.head.appendChild(scriptElement);
    }, []);

    useEffect(() => {
        if (user?.anonymizedId) {
            datadogRum.setUser({ id: user.anonymizedId });
        } else {
            datadogRum.clearUser();
        }
    }, [user]);

    useEffect(() => {
        let relayState;
        if (pathname !== '/' || search) {
            relayState = pathname + (search || '');
        }
        dispatch(fetchAuthenticationAction(relayState));
    }, []);

    useEffect(() => {
        if (user?.id) {
            loadPatientInsurance();
            loadPatientProcesses();
            loadPatientRequestProcesses();
            loadPatientActions();
        }
    }, [user?.id]);

    useEffect(() => {
        if (user?.id && processes) {
            if (isOsasFlowStarted) {
                loadOsasMraReferralLetter();
            }
        }
    }, [user?.id, processes]);

    if (isChecking) {
        return (
            <div>
                <Logo />
            </div>
        );
    }

    if (!loggedIn) {
        return null;
    }

    return (
        <>
            <Timeout />
            <CatchingComponent>
                <Outlet />
            </CatchingComponent>
        </>
    );

    async function loadPatientInsurance() {
        const result = await fetchJson('/api/patient/insurance', 'GET');

        if (result.json) {
            dispatch(receiveUserInsurance(result.json.insurance));
        }
    }

    async function loadPatientProcesses() {
        const result = await fetchJson('/api/patient/processes', 'GET');

        if (result.json) {
            dispatch(receiveUserProcesses(result.json.processes));
        }
    }

    async function loadPatientRequestProcesses() {
        const result = await fetchJson('/api/patient/requestProcesses', 'GET');

        if (result.json) {
            dispatch(receiveUserRequestProcesses(result.json.requests));
            dispatch(
                receiveUserPermissions({
                    canCreateDermaOthProcedure: canUserCreateDermaOthProcedure(result.json.requests),
                    canCreateGhcConsult: canUserCreateGhcConsult(result.json.requests),
                })
            );
        }
    }

    async function loadPatientActions() {
        const result = await fetchJson(`/api/patient/getAvailableActions`, 'GET');

        if (result.json) {
            dispatch(receiveUserActions(result.json.actions));
        }
    }

    async function loadOsasMraReferralLetter() {
        const result = await fetch('/api/patient/osasMraReferralLetter', 'GET');
        const contentType = result.headers.get('content-type');

        if (contentType) {
            if (contentType.includes('application/pdf')) {
                dispatch(receiveUserOsasMraReferralLetter(true));
            }
        }
    }

    function canUserCreateGhcConsult(requests: IRequest[]): boolean {
        return (
            requests &&
            requests.length > 0 &&
            requests.some((request) => isPendingRequestForService(request, ServiceCode.GTO))
        );
    }

    function canUserCreateDermaOthProcedure(requests: IRequest[]): boolean {
        return (
            requests &&
            requests.length > 0 &&
            requests.some((request) => isPendingRequestForService(request, ServiceCode.DermaOth))
        );
    }
}
